import {
  adjustContentVisibility,
  SectionParagraph as Subtitle,
  SectionTitle as Title,
} from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import { useSectionEventOnViewportEntrance } from '@/hooks/useSectionEventOnViewportEntrance';
import { Box, Flex, LegacyText, useViewportSize } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import parse from 'html-react-parser';
import Image from 'next/image';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { Section } from './StyledElements';
import { homeownersImg, petImg, rentersImg, termLifeImg, teslaDesktopImg, teslaMobileImg } from './assets/bundles';
import { CarMode } from './types';
import { CarPageContext } from './CarPageContext';

interface ImageConfig {
  src: string;
  width: number;
  height: number;
}

const imageMap: Record<string, ImageConfig> = {
  homeowners: { src: homeownersImg.src, width: 384, height: 351 },
  renters: { src: rentersImg.src, width: 180, height: 170 },
  pet: { src: petImg.src, width: 180, height: 170 },
  life: { src: termLifeImg.src, width: 180, height: 170 },
  tesla: { src: teslaDesktopImg.src, width: teslaDesktopImg.width, height: teslaDesktopImg.height },
  teslaMobile: { src: teslaMobileImg.src, width: teslaMobileImg.width, height: teslaMobileImg.height },
};

const StyledBundleImage = styled(Image)`
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 12px;
  display: flex;
  max-width: 335px;

  ${MEDIA_QUERIES.mobile`
    flex-direction: row;
    align-items: center;
    width: 355px;
    gap: 16px;
  `}

  ${MEDIA_QUERIES.tabletAndDesktop`
    flex-direction: column;
    align-items: center;
    padding-bottom: 20px;
    justify-content: center;
  `}
`;

const Product = styled(Flex)`
  flex-direction: column;
`;

const ProductsRow = styled(Flex)`
  width: 100%;
  justify-content: center;

  ${MEDIA_QUERIES.mobile`
    flex-direction: column;
    align-items: center;
    gap: 16px;
  `}

  ${MEDIA_QUERIES.tabletAndDesktop`
    flex-direction: row;
    align-items: flex-end;
    gap: 16px;
  `}
`;

const ProductTitle = styled(LegacyText).attrs({
  fontSize: '2rem ',
  fontWeight: 700,
  lineHeight: ['2.4rem', '2.8rem'],
  type: 'main',
})`
  white-space: pre-line;

  ${MEDIA_QUERIES.mobile`
    text-align: left;
  `}
`;

const ProductPrice = styled(LegacyText).attrs({
  fontSize: ['14px', '12px'],
  fontWeight: 'normal',
  fontFamily: 'Lato',
  lineHeight: '20px',
})`
  color: #9b9b9b;

  ${MEDIA_QUERIES.tabletAndDesktop`
    text-transform: uppercase;
  `}
`;

const CardTextContent = styled.div`
  display: flex;
  flex-direction: column;

  ${MEDIA_QUERIES.mobile`
    align-items: flex-start;
  `}

  ${MEDIA_QUERIES.tabletAndDesktop`
    align-items: center;
  `}
`;

const StyledCTA = styled(JoinCTA)`
  display: inline-block;
  min-width: 233px;
`;

const defaultBundlesRow = [
  {
    image: 'renters',
    title: 'Car + Renters',
    desktopTitle: 'Renters',
    price: 'From $5/mo',
    hidden: false,
  },
  {
    image: 'pet',
    title: 'Car + Pet Health',
    desktopTitle: 'Pet',
    price: 'From $10/mo',
    hidden: false,
  },
  {
    image: 'life',
    title: 'Car + Term Life',
    desktopTitle: 'Term life',
    price: 'From $8/mo',
    hidden: false,
  },
];

interface BundleRowItem {
  title: string | undefined;
  desktopTitle?: string | undefined;
  price?: string | undefined;
  image?: string | undefined;
  hidden: boolean | undefined;
}

interface BundlesData {
  main?: { title: string | undefined; image: string | undefined };
  row: BundleRowItem[];
}

const StyledSection = styled(Section)`
  ${adjustContentVisibility};
`;

function getBundleImage(imageType: string | undefined): ImageConfig {
  const key = imageType ?? 'homeowners';
  return imageMap[key];
}

export interface BundlesProps {
  mode?: CarMode;
  title?: string;
  subtitle?: string;
  position?: number;
  bundles?: BundlesData;
  ctaLabel?: string;
  ctaMobileLabel?: string;
  ctaUrl?: string;
  excludedParams?: string[];
}

const Bundles: React.FC<BundlesProps> = ({
  mode = 'default',
  title = 'Save even more with our Car + Home bundle',
  subtitle = "We'll automatically apply your bundle discount to any combination of our products",
  bundles,
  ctaLabel,
  ctaMobileLabel,
  ctaUrl,
  excludedParams,
}) => {
  const { analytics } = useContext(CarPageContext);
  const { isMobileViewport } = useViewportSize();
  const hideBundlesRow = bundles?.row.every(b => b.hidden === true) === true;
  const mainImageKey = mode === 'tesla' ? (isMobileViewport ? 'teslaMobile' : 'tesla') : bundles?.main?.image;
  const mainImage = getBundleImage(mainImageKey);
  const isFsdHomeBundle = mode === 'tesla' && ctaUrl?.includes('home_us_owners');

  const intersectionRef = useSectionEventOnViewportEntrance({
    sectionName: 'bundles',
    page: analytics?.page,
    position: 8,
  });

  return (
    <StyledSection mb={0} pt={[50, 100]} width={1} id="bundles" darkBackground contain="paint" ref={intersectionRef}>
      <Flex gap="15px" flexDirection="column" justifyContent="center">
        <Title tabIndex={0}>{parse(title)}</Title>
        <Box mx="auto" width={['80%', '100%']}>
          <Subtitle tabIndex={0}>{parse(subtitle)}</Subtitle>
        </Box>
      </Flex>
      <Flex flexDirection="column" mt={45} justifyContent="center">
        <StyledBundleImage
          src={mainImage.src}
          role="img"
          alt={bundles?.main?.title ?? 'car and home'}
          width={mainImage.width}
          height={mainImage.height}
          aria-label={`${bundles?.main?.title ?? 'car and home'} insurance bundling`}
        />
        <Flex mt={mode === 'tesla' ? 24 : 20} justifyContent="center" alignItems="center">
          <StyledCTA
            size="small"
            flavor="primary"
            data-source={isFsdHomeBundle ? 'fsd_home_bundle' : analytics.dataSource}
            product={analytics.product}
            customUrl={ctaUrl}
            excludedParams={excludedParams}
          >
            {(isMobileViewport ? ctaMobileLabel : ctaLabel) ?? 'Car + Home'}
          </StyledCTA>
        </Flex>
      </Flex>
      {hideBundlesRow ? null : (
        <ProductsRow mt={40} px={[30, 0]} pb={[50, 100, 100, 100]}>
          {(bundles?.row ?? defaultBundlesRow).map(({ title, desktopTitle, price, image, hidden }, ind) => {
            const rowImage = getBundleImage(image ?? defaultBundlesRow[ind].image);
            const defaultItem = defaultBundlesRow[ind];
            const displayTitle = (isMobileViewport ? title : desktopTitle) ?? desktopTitle ?? defaultItem.desktopTitle;
            const displayPrice = price ?? defaultItem.price;

            return hidden ? null : (
              <Product key={title} justifyContent="center" alignItems="center">
                <ProductCard>
                  <Image src={rowImage.src} alt={displayTitle ?? ''} width={rowImage.width} height={rowImage.height} />
                  <CardTextContent>
                    <ProductTitle mt={[0, '16px']}>{displayTitle}</ProductTitle>
                    <ProductPrice mt="4px">{displayPrice}</ProductPrice>
                  </CardTextContent>
                </ProductCard>
              </Product>
            );
          })}
        </ProductsRow>
      )}
    </StyledSection>
  );
};

export default React.memo(Bundles);
